import { useState } from "react";
import { comments } from "@/mocks/scandal";

const avatarColor = ["bg-primary-100 text-primary-700", "bg-accent-100 text-accent-700", "bg-secondary-100 text-secondary-700"];

function LikeButton({ init }: { init: string }) {
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(parseInt(init, 10));
  return (
    <button
      type="button"
      onClick={() => {
        setLiked((v) => !v);
        setCount((c) => (liked ? c - 1 : c + 1));
      }}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold whitespace-nowrap transition-colors ${
        liked
          ? "border-primary-300 bg-primary-100 text-primary-700"
          : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
      }`}
    >
      <i className={`${liked ? "ri-heart-fill" : "ri-heart-3-line"} text-sm`} />
      {count.toLocaleString()}
    </button>
  );
}

export default function Comments() {
  const [draft, setDraft] = useState("");
  const [posted, setPosted] = useState(false);

  return (
    <section id="discuss" className="border-b border-background-200/70 bg-background-50">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-4 w-1.5 rounded-sm bg-secondary-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-secondary-600">Community Voice</span>
            </div>
            <h2 className="font-heading mt-2 text-2xl font-black text-foreground-950 md:text-3xl">网友讨论 · 热评榜</h2>
            <p className="mt-2 text-sm text-foreground-500">不同的视角同样有参考价值，前排热评代表当前主流讨论走向。</p>
          </div>
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold text-secondary-800 whitespace-nowrap">
            <i className="ri-fire-fill" /> 讨论热度持续
          </span>
        </div>

        {/* 讨论输入（静态演示） */}
        <div className="mt-8 rounded-lg border border-background-200 bg-background-100/50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-background-50">
                我
              </span>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="说说你的看法……（演示输入，不实际提交）"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="button"
              disabled={!draft.trim()}
              onClick={() => {
                setPosted(true);
                setDraft("");
              }}
              className="rounded-md bg-primary-600 px-5 py-2 text-sm font-bold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              发布看法
            </button>
          </div>
          {posted && (
            <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-checkbox-circle-line" /> 已发布到讨论区（演示，未实际发送）
            </p>
          )}
        </div>

        {/* 热评列表 */}
        <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {comments.map((c, i) => (
            <article key={c.author} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center gap-3">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarColor[i % avatarColor.length]}`}>
                  {c.author.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-bold text-foreground-900">{c.author}</h3>
                    {c.hot && (
                      <span className="flex shrink-0 items-center gap-0.5 text-[11px] font-bold text-primary-600 whitespace-nowrap">
                        <i className="ri-fire-fill" /> 热
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-foreground-400">
                    <span className="whitespace-nowrap">{c.role}</span>
                    <span className="whitespace-nowrap">·</span>
                    <span className="rounded bg-background-200 px-1.5 py-px text-[10px] font-bold text-foreground-600 whitespace-nowrap">{c.tag}</span>
                    <span className="whitespace-nowrap">{c.time}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{c.text}</p>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs text-foreground-400">
                  <LikeButton init={c.like} />
                </div>
                <div className="flex items-center gap-3 text-foreground-400">
                  <button type="button" className="flex items-center gap-1 text-xs hover:text-primary-600"><i className="ri-chat-3-line" /> 回复</button>
                  <button type="button" className="flex items-center gap-1 text-xs hover:text-primary-600"><i className="ri-share-forward-line" /> 分享</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}